import { __decorate, __metadata, __param } from "tslib";
import { getSelectTimezone, getSelectCountry } from '@/@core/utils/variables';
import _, { find, each, map, indexOf, filter, isEmpty, join, findIndex, } from 'lodash';
import { E_UserRole } from '@/constants/user';
import { fullLangName } from '@/constants/config';
import { getDateFormatYMD, isExpireNowFromNumber } from '@/utils/date';
import { Symbols } from '@/symbols';
import { inject, injectable } from 'inversify';
import { E_Language, E_UserStatus } from '@/apis/service/types';
import { AsyncCancelError } from '@/utils/exception';
import { phoneCodes } from '@/libs/phoneCode';
import { E_PackageTag, E_PrivliegeLimitationWay } from '../apis/service/PackageApi.types';
let AccountEditService = class AccountEditService {
    constructor(accountApi, privilegeDataService, i18n, miscApi) {
        this.accountApi = accountApi;
        this.privilegeDataService = privilegeDataService;
        this.i18n = i18n;
        this.miscApi = miscApi;
        this.accountInfoData = {};
        this.simpleAccountInfoData = this.getSimpleFormData();
        this.originForm = this.getFormData();
        this.simpleOriginForm = this.getSimpleFormData();
    }
    get countries() {
        return getSelectCountry(fullLangName[this.i18n.locale]);
    }
    get phoneCodes() {
        return map(phoneCodes, (item) => {
            return {
                ...find(this.countries, f => f.value === item.country),
                ...item,
            };
        });
    }
    get timezones() {
        return getSelectTimezone();
    }
    get userRole() {
        return [
            { value: E_UserRole.Admin, text: this.i18n.t('backoffice.company_users.admin') },
            { value: E_UserRole.General, text: this.i18n.t('backoffice.company_users.general') }
        ];
    }
    get languages() {
        const languages = [
            E_Language.en_US,
            E_Language.zh_CN,
            E_Language.zh_TW,
            E_Language.ja_JP
        ];
        return map(languages, item => {
            return {
                value: item,
                label: this.i18n.t(`enum.E_Language.${item}`),
            };
        });
    }
    get companyPrivileges() {
        if (!this.accountInfoData) {
            return [];
        }
        const companyPrivileges = filter(this.accountInfoData.privileges, privilege => {
            return Boolean(privilege.company_privilege_id);
        });
        return this.extendsPrivileges(companyPrivileges);
    }
    get trialPrivileges() {
        if (!this.accountInfoData) {
            return [];
        }
        const trialPrivileges = filter(this.accountInfoData.privileges, privilege => {
            return Boolean(privilege.privilege_id) && !isExpireNowFromNumber(privilege.end_at);
        });
        return this.extendsPrivileges(trialPrivileges);
    }
    get packageInfoList() {
        return this.accountInfoData ? this.accountInfoData.packages : [];
    }
    getPackageName(pkg) {
        return this.privilegeDataService.getPackageName(pkg);
    }
    getSubPackagesText(subPackages) {
        return this.privilegeDataService.getSubPackagesText(subPackages);
    }
    getFormData() {
        const defaultCountryCode = '+86';
        if (this.accountInfoData) {
            const { email, mobile_number, package_end_at_edit_value, privilege_begin_at_edit_value, privilege_end_at_edit_value, privilege_begin_at, privilege_end_at, package_end_at, user_role, ip_restrict, country, 
            // company_country,
            timezone_id, job_role, language, seats, firstname, lastname, nickname, country_code, account_roles, company_id, sso_email_or_mobile_required, company_domain_restriction, company_domains, } = this.accountInfoData;
            const company_privileges = {};
            each(this.companyPrivileges, privilege => {
                const assigned = Boolean(privilege.account_privilege_id);
                company_privileges[privilege.company_privilege_id] = assigned;
            });
            const trial_privileges = {};
            each(this.trialPrivileges, privilege => {
                trial_privileges[privilege.account_privilege_id] = Boolean(privilege.account_privilege_id);
            });
            const packages_assigned = {};
            each(this.packageInfoList || [], pkg => {
                // 如果有这个字段就说明已经分配给用户package
                const assigned = Boolean(pkg.account_package_id);
                packages_assigned[pkg.package_id] = assigned;
            });
            // 默认role都是打开的
            const account_roles_assigned = {};
            each(account_roles || [], value => {
                account_roles_assigned[value.role_id] = true;
            });
            return {
                email,
                mobile_number,
                user_role,
                ip_restrict,
                country,
                country_code: country_code || defaultCountryCode,
                timezone_id,
                job_role,
                language,
                seats,
                firstname,
                lastname,
                nickname,
                enable_privilege_expiry: Boolean(privilege_begin_at_edit_value || privilege_end_at_edit_value),
                privilege_begin_at_edit_value: privilege_begin_at ? getDateFormatYMD(privilege_begin_at) : null,
                privilege_end_at_edit_value: privilege_end_at ? getDateFormatYMD(privilege_end_at) : null,
                enable_package_expiry: Boolean(package_end_at_edit_value),
                package_end_at_value: package_end_at_edit_value ? getDateFormatYMD(package_end_at) : null,
                company_privileges,
                trial_privileges,
                packages_assigned,
                account_roles_assigned,
                company_id,
                sso_email_or_mobile_required,
                company_domain_restriction,
                company_domains,
            };
        }
        return {
            email: '',
            mobile_number: '',
            enable_privilege_expiry: false,
            privilege_begin_at_edit_value: null,
            privilege_end_at_edit_value: null,
            enable_package_expiry: false,
            package_end_at_value: null,
            ip_restrict: false,
            country: null,
            country_code: defaultCountryCode,
            timezone_id: null,
            job_role: '',
            company_privileges: {},
            trial_privileges: {},
            packages: {},
            account_roles: {},
            seats: 1,
            language: null,
            firstname: '',
            lastname: '',
            nickname: '',
            company_id: '',
            sso_email_or_mobile_required: false,
            company_domain_restriction: false,
            company_domains: [],
        };
    }
    getSimpleFormData() {
        const defaultCountryCode = '+86';
        if (!_.isEmpty(this.simpleAccountInfoData)) {
            const { id, company_name, mobile_binding, begin_at_value, end_at_value, account_status, email, mobile_number, user_role, ip_restrict, country, company_country, timezone_id, job_role, language, seats, firstname, lastname, nickname, country_code, company_id, sso_email_or_mobile_required, company_domain_restriction, company_domains, } = this.simpleAccountInfoData;
            return {
                id,
                company_name,
                mobile_binding,
                begin_at_value,
                end_at_value,
                account_status,
                email,
                mobile_number,
                user_role,
                ip_restrict,
                country,
                country_code: country_code || defaultCountryCode,
                timezone_id,
                job_role,
                language,
                seats,
                firstname,
                lastname,
                nickname,
                company_id,
                company_country,
                sso_email_or_mobile_required,
                company_domain_restriction,
                company_domains,
            };
        }
        return {
            email: '',
            id: '',
            company_name: '',
            mobile_binding: false,
            begin_at_value: '',
            mobile_number: '',
            end_at_value: '',
            account_status: E_UserStatus.NO_STATUS,
            ip_restrict: false,
            country: null,
            country_code: defaultCountryCode,
            timezone_id: null,
            job_role: '',
            seats: 1,
            language: null,
            firstname: '',
            lastname: '',
            nickname: '',
            company_id: '',
            sso_email_or_mobile_required: false,
            company_domain_restriction: false,
            company_domains: [],
            user_role: E_UserRole.General,
            company_country: '',
        };
    }
    /**
     * 获取席位数 接口已过滤过期席位
     */
    getSeats(durations) {
        let hasSeats = false;
        const seatList = _.map(durations || [], (item) => {
            const leave = item.seat - item.assign;
            if (leave > 0) {
                hasSeats = true;
            }
            return `${leave} / ${item.seat}`;
        });
        return {
            seatList,
            hasSeats,
        };
    }
    isAnonymousOrOpenApiPrivilege(packages) {
        const res = findIndex(packages, (pkg) => pkg.package_tag === E_PackageTag.OPEN_API || pkg.package_tag === E_PackageTag.ANONYMOUS) > -1;
        return res;
    }
    extendsPrivileges(privileges = []) {
        return _.flatMap(privileges, privilege => {
            if (this.isAnonymousOrOpenApiPrivilege(privilege.packages)) {
                return [];
            }
            const { mainPackages, subPackages } = this.privilegeDataService.splitPackages(privilege.packages);
            const { limitation_ways } = privilege;
            const limitationWaySet = new Set(limitation_ways);
            const ssoFlag = limitationWaySet.has(E_PrivliegeLimitationWay.SSO);
            const domainFlag = limitationWaySet.has(E_PrivliegeLimitationWay.Domains);
            const ipRangeFlag = limitationWaySet.has(E_PrivliegeLimitationWay.IP_Range);
            const { seatList, hasSeats } = this.getSeats(privilege.durations);
            return [{
                    ...privilege,
                    mainPackages,
                    subPackages,
                    limitDomains: domainFlag,
                    checked: !!privilege.account_privilege_id,
                    disabledByDomain: true,
                    // 后端没有返回这里猜测一下
                    business_package_enabled: ssoFlag || domainFlag || ipRangeFlag,
                    seatList,
                    hasSeats,
                }];
        });
    }
    isAnonymousPrivilege(privilege) {
        return this.privilegeDataService.isAnonymousPrivilege({ package_vo_list: privilege.packages });
    }
    isPrivilegeDisabled(privilege) {
        const hasAssigned = this.originForm.company_privileges[privilege.company_privilege_id];
        if (hasAssigned) {
            // 已经分配了 说明可以自由分配关闭
            return false;
        }
        return !privilege.hasSeats;
    }
    isPackageDisabled(pkg) {
        const hasAssigned = this.packageInfoList[pkg.package_id];
        if (hasAssigned) {
            // 已经分配了 说明可以自由分配关闭
            return false;
        }
        return pkg.seat <= pkg.assigned;
    }
    get BusinessPackageLimits() {
        const privilegeIds = this.originForm.company_privileges;
        let IP = false;
        let Domains = false;
        each(privilegeIds, (enabled, pvId) => {
            if (!enabled) {
                return;
            }
            const pv = find(this.companyPrivileges, p => p.company_privilege_id === pvId);
            if (pv && pv.limitation_ways) {
                if (indexOf(pv.limitation_ways, E_PrivliegeLimitationWay.IP_Range) >= 0) {
                    IP = true;
                }
                if (indexOf(pv.limitation_ways, E_PrivliegeLimitationWay.Domains) >= 0) {
                    Domains = true;
                }
            }
        });
        return {
            IP,
            Domains,
        };
    }
    async submitSimpleFormData(formData, vm) {
        const { email, mobile_number, country, country_code, timezone_id, language, nickname, firstname, lastname, id, } = formData;
        let job_role = formData.job_role;
        const simpleOriginForm = this.simpleOriginForm;
        const messages = [];
        if (_.trim(formData.email) !== _.trim(simpleOriginForm.email)) { // 原始数据可能存在空格,trim一下对比,不需要用户重新激活
            messages.push(this.i18n.t('backoffice.company_users.email_check_change_message'));
        }
        if (!isEmpty(messages)) {
            const result = await vm.$swal({
                title: this.i18n.t('backoffice.company_account.need_reactivate_title'),
                html: join(messages, '<br>'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.i18n.t('backoffice.common.yes'),
                cancelButtonText: this.i18n.t('backoffice.common.no'),
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
            });
            if (!result.value) {
                throw new AsyncCancelError();
            }
        }
        if (_.has(job_role, 'system_code')) {
            job_role = job_role.system_code;
        }
        const params = {
            account_id: id,
            email: _.trim(email),
            mobile_number,
            country,
            country_code,
            timezone_id,
            job_role,
            language,
            nickname,
            firstname,
            lastname,
        };
        return this.accountApi.patchAccountEdit(params);
    }
    getTenantUserView(id) {
        return this.accountApi.getTenantUserView(id).then(data => {
            this.accountInfoData = data;
            this.originForm = this.getFormData();
            return this.getFormData();
        });
    }
    patchSimpleAccountEdit(params, vm) {
        return this.submitSimpleFormData(params, vm);
    }
    getSimpleAccountInfoView(id) {
        return this.accountApi.getSimpleAccountInfoView(id).then(data => {
            this.simpleAccountInfoData = data;
            this.simpleOriginForm = this.getSimpleFormData();
            return this.getSimpleFormData();
        });
    }
    getJobRoles() {
        return this.miscApi.getJobRoles();
    }
    getAddAccountPrivilegeParams(item) {
        const { company_privilege_id, account_id, } = item;
        const p = {
            tenant_user_ids: [account_id],
            company_privilege_ids: [company_privilege_id],
        };
        return p;
    }
    postAddAccountPrivilege(params) {
        return this.accountApi.postAddAccountPrivilege(this.getAddAccountPrivilegeParams(params));
    }
    deleteAccountPrivilege(params) {
        return this.accountApi.deleteAccountPrivilege(params);
    }
    deleteAccountPrivilegeTrial(params, accountPrivilegeId) {
        return this.accountApi.deleteAccountPrivilegeTrial(params, accountPrivilegeId);
    }
};
AccountEditService = __decorate([
    injectable(),
    __param(0, inject(Symbols.AccountApi)),
    __param(1, inject(Symbols.PrivilegeDataService)),
    __param(2, inject(Symbols.I18n)),
    __param(3, inject(Symbols.MiscApi)),
    __metadata("design:paramtypes", [Object, Object, Object, Object])
], AccountEditService);
export { AccountEditService };
